// Pure helpers for the workspace chat: turns the chat doc (users/{uid}/workspaceChats/{contentId},
// written only by generateWorkspaceOutput) — or, before a workspace's first message since chat
// memory shipped, the old one-answer-per-tab maps on the workspace doc — into threads to render.
// Mirrors functions/generator/workspaces/chat_context.py; keep the two in step.

// User messages per video, shared by all of its chats. Mirrors message_limit_for in chat_context.py.
export const FREE_MESSAGE_LIMIT = 10;
export const PRO_MESSAGE_LIMIT = 25;
export const PREVIEW_MESSAGE_LIMIT = 2; // a free upload preview, until the account goes Pro
export const MAX_THREADS = 7;
export const MAX_MESSAGE_CHARS = 2000;
export const STALE_BUSY_MS = 11 * 60 * 1000; // the backend gives up after 10 minutes

export function messageLimitFor(plan, workspace) {
  if ((plan?.currentPlan || '').toLowerCase() === 'pro') return PRO_MESSAGE_LIMIT;
  if (workspace?.is_preview) return PREVIEW_MESSAGE_LIMIT;
  return FREE_MESSAGE_LIMIT;
}

const LEGACY_ERROR_PREFIX = '⚠️ Sorry, this prompt could not be processed.';

const toMillis = ts => {
  if (!ts) return null;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  const ms = new Date(ts).getTime();
  return Number.isNaN(ms) ? null : ms;
};

// The pre-chat maps: every tab that got an answer becomes a thread with one finished turn.
export function legacyThreads(workspace = {}) {
  const prompts = workspace.prompt_map || {};
  const outputs = workspace.output_map || {};
  const names = workspace.chat_name_map || {};
  return Object.keys(outputs)
    .filter(id => /^\d{1,3}$/.test(id) && (outputs[id] || '').trim())
    .map(id => {
      const failed = outputs[id].trim().startsWith(LEGACY_ERROR_PREFIX);
      return {
        id,
        name: names[id] || 'New Chat',
        busy: false,
        turns: [{
          id: `legacy${id}`, seq: 0, legacy: true,
          prompt: prompts[id] || '', answer: failed ? '' : outputs[id],
          status: failed ? 'error' : 'done', counted: !failed,
          error: failed ? 'This message could not be answered.' : '',
        }],
      };
    });
}

function threadFromDoc(id, thread, now) {
  const turns = Object.entries(thread.turns || {})
    .map(([turnId, t]) => ({ id: turnId, ...t }))
    .sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0));
  const since = toMillis(thread.busy_since);
  const busy = !!thread.busy_turn && (since == null || now - since < STALE_BUSY_MS);
  return {
    id,
    name: thread.name || 'New Chat',
    busy,
    turns: turns.map(t => (
      // A turn whose request died shows as failed rather than spinning forever.
      !busy && (t.status === 'pending' || t.status === 'streaming')
        ? { ...t, status: 'error', error: 'This message could not be answered.', counted: false }
        : t
    )),
  };
}

// All threads to show, oldest tab first: the server's (or legacy) threads, then chats the user
// opened locally but hasn't sent anything in yet, each with its optimistic (not yet recorded) turns.
export function buildThreads({ chatDoc, workspace, localThreadIds = [], optimistic = {}, now = Date.now() }) {
  const base = chatDoc
    ? Object.entries(chatDoc.threads || {}).map(([id, t]) => threadFromDoc(id, t, now))
    : legacyThreads(workspace);
  const byId = new Map(base.map(t => [t.id, t]));
  localThreadIds.forEach(id => {
    if (!byId.has(id)) byId.set(id, { id, name: 'New Chat', busy: false, turns: [] });
  });
  Object.entries(optimistic).forEach(([turnId, o]) => {
    const thread = byId.get(o.threadId);
    if (!thread) return;
    const existing = thread.turns.find(t => t.id === turnId);
    if (o.retry) {
      // Shown as pending right away, before the server flips the failed turn back.
      if (existing && existing.status === 'error') {
        thread.turns = thread.turns.map(t => (t.id === turnId ? { ...t, status: 'sending', error: '' } : t));
        thread.busy = true;
      }
      return;
    }
    if (existing) return; // the server has it now
    thread.turns = [...thread.turns, { id: turnId, seq: Number.MAX_SAFE_INTEGER, prompt: o.prompt, answer: '', status: 'sending', counted: true }];
    thread.busy = true;
  });
  return [...byId.values()].sort((a, b) => +a.id - +b.id);
}

// Messages used on this video: every counted turn, plus sends the server hasn't recorded yet.
export function countUsed(threads) {
  return threads.reduce((n, t) => n + t.turns.filter(turn => turn.counted || turn.status === 'sending').length, 0);
}

export function nextThreadId(threads) {
  return threads.length ? String(Math.max(...threads.map(t => +t.id)) + 1) : '0';
}

export function newTurnId() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const bytes = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  return Array.from(bytes, b => chars[b % chars.length]).join('');
}

// Ratings live in the workspace doc's feedback_map. Legacy answers keep their old key (the tab id).
export function ratingKey(threadId, turn) {
  return turn.legacy ? threadId : `${threadId}_${turn.id}`;
}

// What to tell the user when the backend refuses a message (the server's text is already phrased for them).
export function sendErrorMessage(error) {
  const code = (error && error.code) || '';
  if (/resource-exhausted|failed-precondition|invalid-argument|not-found/.test(code) && error.message) {
    return error.message;
  }
  return "Your message couldn't be sent. Check your connection and try again.";
}
